import {
  offersProjectStudy,
  projectAwaitsStudy,
  shouldOpenProjectStudyDialog,
} from "@afar-research/afar";
import {
  scopedProjectKey,
  scopeProjectRef,
  scopeThreadRef,
} from "@afar-research/client-runtime/environment";
import { squashAtomCommandFailure } from "@afar-research/client-runtime/state/runtime";
import {
  CommandId,
  DEFAULT_PROVIDER_INTERACTION_MODE,
  DEFAULT_RUNTIME_MODE,
  EnvironmentId,
  MessageId,
  type ScopedProjectRef,
  type ScopedThreadRef,
  ThreadId,
} from "@afar-research/contracts";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { DraftId, useComposerDraftStore } from "../composerDraftStore";
import { useProjectProviderChoice } from "../hooks/useProjectProviderChoice";
import { useOpenProviderSettingsForEnvironment } from "../hooks/useOpenProviderSettingsForEnvironment";
import { randomUUID } from "../lib/utils";
import { useProjectStudyDialogStore } from "../projectStudyDialogStore";
import { commandFailureMessage } from "../rpc/transportError";
import { useProject, useThreadShell, useThreadShellsForProjectRefs } from "../state/entities";
import { projectEnvironment } from "../state/projects";
import { useAtomCommand } from "../state/use-atom-command";
import { buildThreadRouteParams } from "../threadRoutes";
import { ProjectStudyDialog, type ProjectStudyActionResult } from "./ProjectStudyDialog";

/** The project the user is looking at, from the thread or draft in the route. */
function useViewedProjectRef(): ScopedProjectRef | null {
  const pathname = useLocation({ select: (location) => location.pathname });
  const [, first, second] = pathname.split("/").map((part) => decodeURIComponent(part ?? ""));
  const threadRef =
    first && second && first !== "draft"
      ? scopeThreadRef(EnvironmentId.make(first), ThreadId.make(second))
      : null;
  const thread = useThreadShell(threadRef);
  const draft = useComposerDraftStore((store) =>
    first === "draft" && second ? store.getDraftSession(DraftId.make(second)) : null,
  );
  if (thread !== null) return scopeProjectRef(thread.environmentId, thread.projectId);
  if (draft?.environmentId && draft.projectId) {
    return scopeProjectRef(draft.environmentId, draft.projectId);
  }
  return null;
}

/**
 * Asks what to study once a project created from references has ingested its
 * references, on whatever client is showing it. The dialog opens again from
 * the sidebar, the ingest thread and the empty thread after the user closes it.
 */
export function ProjectStudyCoordinator() {
  const viewed = useViewedProjectRef();
  const { target, open, close } = useProjectStudyDialogStore();
  const viewedRefs = useMemo(() => (viewed === null ? [] : [viewed]), [viewed]);
  const viewedKey = viewed === null ? null : scopedProjectKey(viewed);
  const viewedProject = useProject(viewed);
  const viewedThreads = useThreadShellsForProjectRefs(viewedRefs);

  // Closing the dialog sets it aside for this viewing of the project only:
  // opening another project and coming back asks again.
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  useEffect(() => {
    if (viewedKey !== null && dismissedKey !== null && viewedKey !== dismissedKey) {
      setDismissedKey(null);
    }
  }, [dismissedKey, viewedKey]);
  const dismiss = () => {
    if (target !== null) setDismissedKey(scopedProjectKey(target));
    close();
  };

  useEffect(() => {
    if (viewed === null || viewedProject === null || target !== null) return;
    if (viewedKey === dismissedKey) return;
    if (shouldOpenProjectStudyDialog(viewedProject, viewedThreads)) open(viewed);
  }, [dismissedKey, open, target, viewed, viewedKey, viewedProject, viewedThreads]);

  const project = useProject(target);
  const provider = useProjectProviderChoice(target?.environmentId ?? null);
  const openProviderSettings = useOpenProviderSettingsForEnvironment();
  const startStudy = useAtomCommand(projectEnvironment.startStudy, { reportFailure: false });
  const skipStudy = useAtomCommand(projectEnvironment.skipStudy, { reportFailure: false });
  const navigate = useNavigate();
  const [identities, setIdentities] = useState(() => ({
    commandId: randomUUID(),
    threadId: randomUUID(),
    messageId: randomUUID(),
  }));

  // The study thread opens once it reaches this client, so the route never
  // lands on a thread that does not exist here yet.
  const [studyThread, setStudyThread] = useState<ScopedThreadRef | null>(null);
  const studyThreadShell = useThreadShell(studyThread);
  useEffect(() => {
    if (studyThread === null || studyThreadShell === null) return;
    setStudyThread(null);
    void navigate({ to: "/$environmentId/$threadId", params: buildThreadRouteParams(studyThread) });
  }, [navigate, studyThread, studyThreadShell]);

  // A study the user already started, or a project that no longer exists, has nothing to ask.
  const showing = target !== null && project !== null && offersProjectStudy(project);

  const start = async (study: string): Promise<ProjectStudyActionResult> => {
    if (target === null || provider.modelSelection === null) {
      return { _tag: "Failure", message: "A ready Agent Provider is required to start the study." };
    }
    const threadId = ThreadId.make(identities.threadId);
    const result = await startStudy({
      environmentId: target.environmentId,
      input: {
        commandId: CommandId.make(identities.commandId),
        projectId: target.projectId,
        threadId,
        messageId: MessageId.make(identities.messageId),
        study,
        modelSelection: provider.modelSelection,
        runtimeMode: DEFAULT_RUNTIME_MODE,
        interactionMode: DEFAULT_PROVIDER_INTERACTION_MODE,
      },
    });
    if (result._tag === "Failure") {
      return {
        _tag: "Failure",
        message: commandFailureMessage(
          squashAtomCommandFailure(result),
          "The study did not start.",
        ),
      };
    }
    close();
    setIdentities({ commandId: randomUUID(), threadId: randomUUID(), messageId: randomUUID() });
    setStudyThread(scopeThreadRef(target.environmentId, threadId));
    return { _tag: "Success" };
  };

  const skip = async (): Promise<ProjectStudyActionResult> => {
    if (target === null) return { _tag: "Success" };
    const result = await skipStudy({
      environmentId: target.environmentId,
      input: { projectId: target.projectId },
    });
    if (result._tag === "Failure") {
      return {
        _tag: "Failure",
        message: commandFailureMessage(
          squashAtomCommandFailure(result),
          "The study was not skipped.",
        ),
      };
    }
    close();
    return { _tag: "Success" };
  };

  return (
    <ProjectStudyDialog
      {...provider.providerProps}
      canSkip={project !== null && projectAwaitsStudy(project)}
      key={target === null ? "closed" : scopedProjectKey(target)}
      onClose={dismiss}
      onOpenProviderSettings={
        provider.providerProps.managedByAfar || target === null
          ? undefined
          : () => {
              dismiss();
              void openProviderSettings(target.environmentId);
            }
      }
      onSkip={skip}
      onStart={start}
      open={showing}
      projectTitle={project?.title ?? ""}
    />
  );
}
